import React, { Component } from 'react';
import { TextField, IconButton, InputAdornment, Typography, LinearProgress } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

/**
 * Input de senha com checklist visual de força.
 * Critérios obrigatórios (mesmos do backend, em StrongPasswordValidator.cs):
 *   • mínimo 12 caracteres
 *   • ≥ 1 letra maiúscula
 *   • ≥ 1 número
 *   • ≥ 1 caractere especial
 *
 * Props:
 *   value, onChange, label, name, required, fullWidth (default true)
 *   onValidityChange(isValid) — chamado quando passa a atender (ou deixa de atender) os 4 critérios
 *   helperLabel — texto auxiliar opcional acima da checklist
 */
const SPECIALS = "!@#$%^&*()-_=+[]{};:'\",.<>/?\\|`~";
const MIN_LENGTH = 12;

const evaluate = (pwd) => {
    const v = pwd || '';
    return {
        length: v.length >= MIN_LENGTH,
        upper: /[A-Z]/.test(v),
        digit: /[0-9]/.test(v),
        special: v.split('').some(ch => SPECIALS.indexOf(ch) >= 0),
    };
};

const allValid = (criteria) => criteria.length && criteria.upper && criteria.digit && criteria.special;

class PasswordStrengthInput extends Component {
    constructor(props) {
        super(props);
        this.state = { showPassword: false };
        this._lastValid = null;
    }

    componentDidMount() {
        this.notifyValidity();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) this.notifyValidity();
    }

    notifyValidity = () => {
        const isValid = allValid(evaluate(this.props.value));
        if (this._lastValid !== isValid) {
            this._lastValid = isValid;
            if (this.props.onValidityChange) this.props.onValidityChange(isValid);
        }
    }

    toggleShow = () => this.setState(s => ({ showPassword: !s.showPassword }));

    renderRule(label, ok) {
        const Icon = ok ? CheckCircleIcon : RadioButtonUncheckedIcon;
        const color = ok ? '#2e7d32' : '#9e9e9e';
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color, marginTop: 2 }}>
                <Icon style={{ fontSize: 16 }} />
                <span>{label}</span>
            </div>
        );
    }

    strengthLabel(criteria) {
        const score = [criteria.length, criteria.upper, criteria.digit, criteria.special].filter(Boolean).length;
        if (score <= 1) return { label: 'Muito fraca', color: '#d32f2f', percent: 25 };
        if (score === 2) return { label: 'Fraca',       color: '#f57c00', percent: 50 };
        if (score === 3) return { label: 'Média',       color: '#fbc02d', percent: 75 };
        return { label: 'Forte', color: '#2e7d32', percent: 100 };
    }

    render() {
        const { value, onChange, label, name, required, fullWidth = true, helperLabel } = this.props;
        const criteria = evaluate(value);
        const strength = this.strengthLabel(criteria);
        return (
            <div>
                <TextField
                    name={name}
                    label={label}
                    type={this.state.showPassword ? 'text' : 'password'}
                    value={value || ''}
                    onChange={onChange}
                    required={required}
                    fullWidth={fullWidth}
                    autoComplete="new-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={this.toggleShow} edge="end" tabIndex={-1}>
                                    {this.state.showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {value && value.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <LinearProgress
                                variant="determinate"
                                value={strength.percent}
                                style={{
                                    flex: 1,
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: '#e0e0e0',
                                }}
                                classes={{ bar: 'pwd-strength-bar' }}
                            />
                            <Typography variant="caption" style={{ color: strength.color, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                                {strength.label}
                            </Typography>
                        </div>
                        <style>{`.pwd-strength-bar { background-color: ${strength.color} !important; }`}</style>
                    </div>
                )}
                {helperLabel && (
                    <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 6 }}>
                        {helperLabel}
                    </Typography>
                )}
                <div style={{ marginTop: 6 }}>
                    {this.renderRule(`Mínimo ${MIN_LENGTH} caracteres`, criteria.length)}
                    {this.renderRule('Pelo menos 1 letra maiúscula (A-Z)', criteria.upper)}
                    {this.renderRule('Pelo menos 1 número (0-9)', criteria.digit)}
                    {this.renderRule('Pelo menos 1 caractere especial (!@#$...)', criteria.special)}
                </div>
            </div>
        );
    }
}

export default PasswordStrengthInput;
